import {
	buildShopifyCartRequestDiagnostic,
	type ShopifyCartOperation,
	type ShopifyCartRequestDiagnostic,
} from './cart-diagnostics';

const SHOPIFY_STORE_DOMAIN = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN ?? '';
const SHOPIFY_STOREFRONT_ACCESS_TOKEN =
	process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN ?? '';

const endpoint = `https://${SHOPIFY_STORE_DOMAIN}/api/2026-04/graphql.json`;

export interface ShopifyRequestOptions {
	/** Optional request-scoped executor for server cart mutations. */
	executeGraphql?: ShopifyGraphqlExecutor;
	headers?: HeadersInit;
	/** Receives a copy of successful upstream response headers for this request only. */
	onResponseHeaders?: (headers: Headers) => void;
	/** Opt-in, redacted metadata from the final cart fetch; never receives raw headers. */
	onCartRequestDiagnostic?: (diagnostic: ShopifyCartRequestDiagnostic) => void;
}

export interface ShopifyFetchOptions extends ShopifyRequestOptions {
	query: string;
	variables?: object;
	cache?: RequestCache;
	tags?: string[];
	revalidate?: number | false;
	cartOperation?: ShopifyCartOperation;
}

export type ShopifyGraphqlExecutor = <T>(options: ShopifyFetchOptions) => Promise<T>;

interface ShopifyResponse<T> {
	data: T;
	errors?: {
		message: string;
		locations?: { line: number; column: number }[];
	}[];
}

function appendRequestHeaders(target: Headers, source?: HeadersInit) {
	if (!source) {
		return;
	}

	const entries =
		source instanceof Headers
			? source.entries()
			: Array.isArray(source)
				? source
				: Object.entries(source);

	for (const [key, value] of entries) {
		target.set(key, value);
	}
}

// Shopify's own session/consent cookies. The Storefront API ties cart and
// checkout requests to the shopper's session through these (Hydrogen forwards
// them the same way); every other browser cookie is dropped.
const SHOPIFY_COOKIE_PATTERN =
	/^(?:_shopify_essential|_shopify_analytics|_shopify_marketing|_shopify_y|_shopify_s|_tracking_consent)$/;

function removeForwardedBrowserHeaders(headers: Headers) {
	for (const name of ['content-length', 'accept-encoding']) {
		headers.delete(name);
	}

	const shopifyCookies = (headers.get('cookie') ?? '')
		.split(';')
		.map((cookie) => cookie.trim())
		.filter((cookie) => SHOPIFY_COOKIE_PATTERN.test(cookie.split('=')[0] ?? ''))
		.join('; ');
	if (shopifyCookies) {
		headers.set('cookie', shopifyCookies);
	} else {
		headers.delete('cookie');
	}
}

function buildFetchOptions({
	query,
	variables,
	headers,
	cache,
	tags,
	revalidate,
}: ShopifyFetchOptions) {
	const mergedHeaders = new Headers({
		'Content-Type': 'application/json',
		'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_ACCESS_TOKEN,
	});

	appendRequestHeaders(mergedHeaders, headers);
	// Server callers may supply private authentication. Never send both token
	// types, and never read a private environment variable in this shared module.
	if (mergedHeaders.get('Shopify-Storefront-Private-Token')?.trim()) {
		mergedHeaders.delete('X-Shopify-Storefront-Access-Token');
	} else {
		mergedHeaders.delete('Shopify-Storefront-Private-Token');
	}

	// Remove browser-specific headers that must not be forwarded to the Storefront API.
	// content-length: wrong size (browser body != GraphQL body we construct here)
	// cookie: reduced to Shopify's session/consent cookies
	// accept-encoding: server-side fetch handles its own encoding negotiation
	removeForwardedBrowserHeaders(mergedHeaders);

	const fetchOptions: RequestInit & {
		next?: { tags?: string[]; revalidate?: number | false };
	} = {
		method: 'POST',
		headers: mergedHeaders,
		body: JSON.stringify({ query, variables }),
	};

	if (cache) {
		fetchOptions.cache = cache;
	}

	if (tags || revalidate !== undefined) {
		fetchOptions.next = {};
		if (tags) fetchOptions.next.tags = tags;
		if (revalidate !== undefined) fetchOptions.next.revalidate = revalidate;
	}

	return fetchOptions;
}

/**
 * Resilience config for the Storefront API fetch.
 *
 * Every storefront product/collection read funnels through `shopifyFetch`, and
 * the prerendered pages are wrapped in `'use cache'`. When the Vercel build
 * cache is warm those reads are cache hits; when it is cold (e.g. a "redeploy
 * without cache"), all ~750 prerendered pages issue live requests at once.
 * Unbounded, that burst produces ETIMEDOUT / "Connection closed" connect
 * failures that abort the export. To survive transient upstream blips we add:
 *   - a concurrency cap (build phase only, so runtime request latency is
 *     untouched) to avoid the connection storm,
 *   - a per-attempt timeout so a hung connection fails fast instead of stalling
 *     a worker, and
 *   - a bounded retry with backoff for connect-level errors.
 * All three are env-overridable. Caching is unaffected: requests are POST (not
 * Next Data-Cached) and the real caching layer is the surrounding `'use cache'`.
 */
const SHOPIFY_FETCH_TIMEOUT_MS = toPositiveInt(process.env.SHOPIFY_FETCH_TIMEOUT_MS, 10_000);
const SHOPIFY_FETCH_MAX_ATTEMPTS = toPositiveInt(process.env.SHOPIFY_FETCH_MAX_ATTEMPTS, 3);
const SHOPIFY_FETCH_MAX_CONCURRENCY = toPositiveInt(
	process.env.SHOPIFY_FETCH_MAX_CONCURRENCY,
	process.env.NEXT_PHASE === 'phase-production-build' ? 8 : Number.POSITIVE_INFINITY,
);

const RETRYABLE_NETWORK_CODES = new Set([
	'ETIMEDOUT',
	'ECONNRESET',
	'ECONNREFUSED',
	'EAI_AGAIN',
	'ENETUNREACH',
	'EPIPE',
	'UND_ERR_CONNECT_TIMEOUT',
	'UND_ERR_SOCKET',
]);

function toPositiveInt(value: string | undefined, fallback: number): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

class ShopifyHttpError extends Error {
	readonly status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = 'ShopifyHttpError';
		this.status = status;
	}
}

let activeRequestCount = 0;
const pendingRequestResolvers: Array<() => void> = [];

function acquireRequestSlot(): Promise<void> {
	if (activeRequestCount < SHOPIFY_FETCH_MAX_CONCURRENCY) {
		activeRequestCount += 1;
		return Promise.resolve();
	}

	return new Promise<void>((resolve) => {
		pendingRequestResolvers.push(() => {
			activeRequestCount += 1;
			resolve();
		});
	});
}

function releaseRequestSlot(): void {
	activeRequestCount -= 1;
	const next = pendingRequestResolvers.shift();
	if (next) next();
}

function getErrorCode(error: Error): string | undefined {
	const cause = (error as { cause?: unknown }).cause;
	if (cause && typeof cause === 'object' && 'code' in cause) {
		const code = (cause as { code?: unknown }).code;
		if (typeof code === 'string') return code;
	}

	const directCode = (error as { code?: unknown }).code;
	return typeof directCode === 'string' ? directCode : undefined;
}

/**
 * Retry only transient failures: connect-level network errors, our own request
 * timeout, and 429/5xx responses. GraphQL errors and other 4xx are deterministic
 * and rethrown immediately so we don't mask real bugs behind retries.
 */
function isRetryableShopifyError(error: unknown): boolean {
	if (error instanceof ShopifyHttpError) {
		return error.status === 429 || error.status >= 500;
	}

	if (error instanceof Error) {
		// `AbortSignal.timeout` rejects with a TimeoutError; manual aborts use AbortError.
		if (error.name === 'TimeoutError' || error.name === 'AbortError') return true;

		// undici surfaces connect failures as TypeError('fetch failed') (the
		// happy-eyeballs ETIMEDOUT AggregateError lands here) and mid-stream drops
		// as "Connection closed." / "terminated" / "other side closed".
		const message = error.message.toLowerCase();
		if (
			message.includes('fetch failed') ||
			message.includes('connection closed') ||
			message.includes('terminated') ||
			message.includes('other side closed')
		) {
			return true;
		}

		const code = getErrorCode(error);
		if (code && RETRYABLE_NETWORK_CODES.has(code)) return true;
	}

	return false;
}

function getRetryDelayMs(attempt: number): number {
	// Exponential backoff with jitter: ~0.3s, ~0.6s, ~1.2s (+ up to 25% jitter).
	const base = 300 * 2 ** (attempt - 1);
	return base + base * 0.25 * Math.random();
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

async function runShopifyRequest<T>(options: ShopifyFetchOptions): Promise<T> {
	await acquireRequestSlot();

	try {
		const init = {
			...buildFetchOptions(options),
			signal: AbortSignal.timeout(SHOPIFY_FETCH_TIMEOUT_MS),
		};
		let response: Response;
		try {
			response = await fetch(endpoint, init);
		} catch (error) {
			emitCartRequestDiagnostic(options, init.headers);
			throw error;
		}
		emitCartRequestDiagnostic(options, init.headers, response);

		if (!response.ok) {
			const text = await response.text();
			throw new ShopifyHttpError(
				response.status,
				`Shopify API error: ${response.status} ${response.statusText}\n${text}`,
			);
		}

		const json: ShopifyResponse<T> = await response.json();

		if (json.errors) {
			const messages = json.errors.map((e) => e.message).join('\n');
			throw new Error(`Shopify GraphQL errors:\n${messages}`);
		}

		options.onResponseHeaders?.(new Headers(response.headers));
		return json.data;
	} finally {
		releaseRequestSlot();
	}
}

function emitCartRequestDiagnostic(
	options: ShopifyFetchOptions,
	headers: HeadersInit | undefined,
	response?: Response,
) {
	if (!options.cartOperation || !options.onCartRequestDiagnostic) return;
	try {
		options.onCartRequestDiagnostic(
			buildShopifyCartRequestDiagnostic(
				options.cartOperation,
				endpoint,
				new Headers(headers),
				response,
			),
		);
	} catch {
		// Diagnostics must not change cart success, failure, or retry behavior.
	}
}

export async function shopifyFetch<T>(options: ShopifyFetchOptions): Promise<T> {
	let lastError: unknown;

	for (let attempt = 1; attempt <= SHOPIFY_FETCH_MAX_ATTEMPTS; attempt += 1) {
		try {
			return await runShopifyRequest<T>(options);
		} catch (error) {
			lastError = error;
			if (attempt >= SHOPIFY_FETCH_MAX_ATTEMPTS || !isRetryableShopifyError(error)) {
				throw error;
			}
			await delay(getRetryDelayMs(attempt));
		}
	}

	// Unreachable: the loop either returns a value or throws above.
	throw lastError;
}

/**
 * Helper to remove edges/node wrappers from Shopify connections.
 */
export function flattenConnection<T>(connection: { edges: { node: T }[] }): T[] {
	return connection.edges.map((edge) => edge.node);
}
